import React, { useState, useEffect, useCallback } from "react";
import { X, CreditCard, Smartphone, Copy, Check, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";

const fmtMoney = (n, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c }).format(Number(n || 0));

const CopyChip = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Browsers without async clipboard fall back silently — user can still
      // select the value manually since it's plain text.
    }
  };
  return (
    <div className="bg-[#0C0E12] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-3"
         data-testid={testid}>
      <div>
        <div className="text-xs text-[#A29F9B] uppercase tracking-widest mb-1">{label}</div>
        <div className="font-mono text-lg text-[#F5F2EE]">{value}</div>
      </div>
      <button onClick={copy}
              className="p-2 rounded-lg border border-white/10 hover:border-[#C5A880] text-[#A29F9B] hover:text-[#C5A880] transition-colors"
              title="Copiar">
        {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

/**
 * EuPagoModal — one-off payment flow via Multibanco or MBWAY.
 * Used as a replacement for the Stripe one-off checkout (mode="payment").
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   packageId: "one_off_month" | "one_off_year"
 *   amount, currency, label  — purely display
 *   billing: object from BillingModal (sent server-side to populate Moloni)
 *   coupon: string | null
 *   onPaid: () => void  — called once the webhook confirms payment
 */
const EuPagoModal = ({ open, onClose, packageId, amount, currency, label,
                       billing, coupon, onPaid }) => {
  const [method, setMethod] = useState("multibanco");
  // multibanco/mbway state shared lifecycle: idle → creating → waiting → paid|failed
  const [stage, setStage] = useState("idle");
  const [error, setError] = useState(null);

  // Multibanco
  const [mbRef, setMbRef] = useState(null);   // {entity, reference, amount, deadline, session_id}

  // MBWAY
  const [phone, setPhone] = useState("");
  const [mbwaySession, setMbwaySession] = useState(null);  // session_id

  useEffect(() => {
    if (!open) {
      setStage("idle");
      setMbRef(null);
      setMbwaySession(null);
      setError(null);
      setMethod("multibanco");
    }
  }, [open]);

  const createMultibanco = async () => {
    setStage("creating");
    setError(null);
    try {
      const r = await client.post("/eupago/multibanco/create",
        { package_id: packageId, billing, coupon: coupon || null });
      setMbRef(r.data);
      setStage("waiting");
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha a criar referência.");
      setStage("failed");
    }
  };

  const createMbway = async () => {
    if (!/^9\d{8}$/.test(phone.replace(/\s/g, ""))) {
      toast.error("Indica um número de 9 dígitos a começar por 9.");
      return;
    }
    setStage("creating");
    setError(null);
    try {
      const r = await client.post("/eupago/mbway/create",
        { package_id: packageId, billing, coupon: coupon || null,
          phone: phone.replace(/\s/g, "") });
      setMbwaySession(r.data);
      setStage("waiting");
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha a iniciar MBWAY.");
      setStage("failed");
    }
  };

  // Poll status while waiting (every 4s, for 10 min). Webhook is authoritative —
  // this just refreshes the UI quickly when payment lands.
  const sessionId = mbRef?.session_id || mbwaySession?.session_id;
  const pollStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await client.get(`/eupago/status/${sessionId}`);
      if (r.data?.payment_status === "paid") {
        setStage("paid");
        toast.success("Pagamento confirmado! Bem-vindo ao Premium.");
        setTimeout(() => onPaid?.(), 1200);
      } else if (["failed", "expired", "cancel", "canceled", "error"].includes(r.data?.payment_status)) {
        setStage("failed");
        setError("Pagamento não foi concluído.");
      }
    } catch {
      // Transient — keep polling.
    }
  }, [sessionId, onPaid]);

  useEffect(() => {
    if (stage !== "waiting" || !sessionId) return undefined;
    const t = setInterval(pollStatus, 4000);
    // Stop polling after 10 min to avoid burning bandwidth.
    const stop = setTimeout(() => clearInterval(t), 10 * 60 * 1000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [stage, sessionId, pollStatus]);

  if (!open) return null;

  const showTabs = stage === "idle" || stage === "failed";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose}
         data-testid="eupago-backdrop">
      <div className="bg-[#161920] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="eupago-modal">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="label-uppercase">Pagamento único · EuPago</span>
            <h3 className="font-display text-2xl mt-2 text-[#F5F2EE]">{label}</h3>
            <p className="text-sm text-[#A29F9B] mt-1">Total: <strong className="text-[#C5A880]">{fmtMoney(amount, currency?.toUpperCase())}</strong></p>
          </div>
          <button onClick={onClose} className="text-[#A29F9B] hover:text-[#F5F2EE]"
                  data-testid="eupago-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {showTabs && (
          <div className="flex gap-2 mb-5">
            <button onClick={() => setMethod("multibanco")}
                    className={`flex-1 py-2 rounded-xl text-xs uppercase tracking-wider transition-colors inline-flex items-center justify-center gap-1.5 ${
                      method === "multibanco" ? "bg-[#C5A880]/15 border border-[#C5A880] text-[#F5F2EE]"
                                              : "bg-transparent border border-white/10 text-[#A29F9B] hover:border-white/30"
                    }`}
                    data-testid="eupago-tab-multibanco">
              <CreditCard className="w-3.5 h-3.5" /> Multibanco
            </button>
            <button onClick={() => setMethod("mbway")}
                    className={`flex-1 py-2 rounded-xl text-xs uppercase tracking-wider transition-colors inline-flex items-center justify-center gap-1.5 ${
                      method === "mbway" ? "bg-[#C5A880]/15 border border-[#C5A880] text-[#F5F2EE]"
                                         : "bg-transparent border border-white/10 text-[#A29F9B] hover:border-white/30"
                    }`}
                    data-testid="eupago-tab-mbway">
              <Smartphone className="w-3.5 h-3.5" /> MB WAY
            </button>
          </div>
        )}

        {/* IDLE — show CTA for selected method */}
        {stage === "idle" && method === "multibanco" && (
          <div className="space-y-3">
            <p className="text-sm text-[#A29F9B]">
              Vamos gerar uma <strong>referência Multibanco</strong> que podes pagar em qualquer
              ATM ou no teu homebanking. Recebes acesso Premium assim que o pagamento for confirmado.
            </p>
            <button onClick={createMultibanco}
                    className="btn-primary w-full"
                    data-testid="eupago-mb-create">
              Gerar referência Multibanco
            </button>
          </div>
        )}

        {stage === "idle" && method === "mbway" && (
          <div className="space-y-3">
            <p className="text-sm text-[#A29F9B]">
              Indica o número de telemóvel registado na app MB WAY. Vais receber uma notificação para confirmar.
            </p>
            <input type="tel" value={phone}
                   onChange={(e) => setPhone(e.target.value)}
                   placeholder="9XX XXX XXX"
                   className="w-full bg-[#0C0E12] border border-white/10 rounded-xl px-4 py-3 text-lg text-[#F5F2EE] tracking-wider"
                   data-testid="eupago-mbway-phone" />
            <button onClick={createMbway}
                    className="btn-primary w-full"
                    data-testid="eupago-mbway-create">
              Pagar com MB WAY
            </button>
          </div>
        )}

        {stage === "creating" && (
          <div className="text-center py-8" data-testid="eupago-creating">
            <Loader2 className="w-6 h-6 text-[#C5A880] mx-auto mb-3 animate-spin" />
            <p className="text-sm text-[#A29F9B]">A contactar EuPago…</p>
          </div>
        )}

        {/* WAITING — show reference or "check phone" */}
        {stage === "waiting" && mbRef && (
          <div className="space-y-3" data-testid="eupago-mb-waiting">
            <p className="text-sm text-[#DCD7D2]">
              Paga em qualquer ATM ou homebanking. Confirmação automática.
            </p>
            <CopyChip label="Entidade" value={mbRef.entity} testid="eupago-mb-entity" />
            <CopyChip label="Referência" value={mbRef.reference} testid="eupago-mb-reference" />
            <CopyChip label="Montante" value={fmtMoney(mbRef.amount, "EUR")} testid="eupago-mb-amount" />
            <div className="text-xs text-[#A29F9B] bg-[#0C0E12]/60 rounded-lg p-3 border border-white/5">
              Pagável até <strong className="text-[#F5F2EE]">{mbRef.deadline}</strong>.
              A página fica à espera — assim que pagares, vais ver "Pagamento confirmado".
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-[#A29F9B] py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> À espera da confirmação…
            </div>
          </div>
        )}

        {stage === "waiting" && mbwaySession && (
          <div className="text-center py-8 space-y-3" data-testid="eupago-mbway-waiting">
            <Smartphone className="w-10 h-10 text-[#C5A880] mx-auto" />
            <h4 className="font-display text-xl">Verifica o teu telemóvel</h4>
            <p className="text-sm text-[#A29F9B] max-w-sm mx-auto">
              Acabaste de receber uma notificação na app MB WAY. Confirma o pagamento e
              volta aqui — a página atualiza-se sozinha.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-[#A29F9B] pt-3">
              <Loader2 className="w-3 h-3 animate-spin" /> À espera da confirmação…
            </div>
          </div>
        )}

        {stage === "paid" && (
          <div className="text-center py-8 space-y-3" data-testid="eupago-paid">
            <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto" />
            <h4 className="font-display text-2xl text-[#F5F2EE]">Pagamento confirmado!</h4>
            <p className="text-sm text-[#A29F9B]">
              Premium ativado. A fatura segue por email em alguns segundos.
            </p>
          </div>
        )}

        {stage === "failed" && (
          <div className="text-center py-6 space-y-3" data-testid="eupago-failed">
            <AlertCircle className="w-10 h-10 text-rose-300 mx-auto" />
            <p className="text-sm text-rose-300 max-w-sm mx-auto">{error || "Algo correu mal."}</p>
            <button onClick={() => { setStage("idle"); setError(null); }}
                    className="text-xs text-[#C5A880] hover:underline"
                    data-testid="eupago-retry">
              Tentar novamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EuPagoModal;
